import BaseService from './BaseService'
import { api } from '@/boot/axios'

class BookingService extends BaseService {
  constructor () {
    super('/booking')
  }

  async getSettings () {
    const res = await api.get(`${this.baseURL}/settings`)
    return res.data
  }

  async updateSettings (payload) {
    const res = await api.put(`${this.baseURL}/settings`, payload)
    return res.data
  }

  async listFleetPeriodRules () {
    const res = await api.get(`${this.baseURL}/fleet/period-rules`)
    return res.data
  }

  async createFleetPeriodRule (payload) {
    const res = await api.post(`${this.baseURL}/fleet/period-rules`, payload)
    return res.data
  }

  async updateFleetPeriodRule (id, payload) {
    const res = await api.put(`${this.baseURL}/fleet/period-rules/${id}`, payload)
    return res.data
  }

  async deleteFleetPeriodRule (id) {
    await api.delete(`${this.baseURL}/fleet/period-rules/${id}`)
  }

  async listFleetServiceRules (params = {}) {
    const res = await api.get(`${this.baseURL}/fleet/service-rules`, { params })
    return res.data
  }

  async createFleetServiceRule (payload) {
    const res = await api.post(`${this.baseURL}/fleet/service-rules`, payload)
    return res.data
  }

  async updateFleetServiceRule (id, payload) {
    const res = await api.put(`${this.baseURL}/fleet/service-rules/${id}`, payload)
    return res.data
  }

  async deleteFleetServiceRule (id) {
    await api.delete(`${this.baseURL}/fleet/service-rules/${id}`)
  }

  async listAppointments (params = {}) {
    const res = await api.get(`${this.baseURL}/appointments`, { params })
    return res.data
  }

  async getAppointment (id) {
    const res = await api.get(`${this.baseURL}/appointments/${id}`)
    return res.data
  }

  async createAppointment (payload) {
    const res = await api.post(`${this.baseURL}/appointments`, payload)
    return res.data
  }

  async updateAppointment (id, payload) {
    const res = await api.put(`${this.baseURL}/appointments/${id}`, payload)
    return res.data
  }

  async patchAppointmentStatus (id, payload) {
    const res = await api.patch(`${this.baseURL}/appointments/${id}/status`, payload)
    return res.data
  }

  async deleteAppointment (id) {
    await api.delete(`${this.baseURL}/appointments/${id}`)
  }

  async listSlots (params) {
    const res = await api.get(`${this.baseURL}/slots`, { params })
    return res.data
  }

  async getSlotsCalendarSummary (params) {
    const res = await api.get(`${this.baseURL}/slots/calendar-summary`, { params })
    return res.data
  }

  async getSlotsByDate (params) {
    const res = await api.get(`${this.baseURL}/slots/by-date`, { params })
    return res.data
  }

  async listServices (params = {}) {
    const res = await api.get(`${this.baseURL}/services`, { params })
    return res.data
  }

  async getService (id) {
    const res = await api.get(`${this.baseURL}/services/${id}`)
    return res.data
  }

  async createService (payload) {
    const res = await api.post(`${this.baseURL}/services`, payload)
    return res.data
  }

  async updateService (id, payload) {
    const res = await api.put(`${this.baseURL}/services/${id}`, payload)
    return res.data
  }

  async deleteService (id) {
    await api.delete(`${this.baseURL}/services/${id}`)
  }

  async listStaff (params = {}) {
    const res = await api.get(`${this.baseURL}/staff`, { params })
    return res.data
  }

  async getStaff (id) {
    const res = await api.get(`${this.baseURL}/staff/${id}`)
    return res.data
  }

  async createStaff (payload) {
    const res = await api.post(`${this.baseURL}/staff`, payload)
    return res.data
  }

  async updateStaff (id, payload) {
    const res = await api.put(`${this.baseURL}/staff/${id}`, payload)
    return res.data
  }

  async deleteStaff (id) {
    await api.delete(`${this.baseURL}/staff/${id}`)
  }

  async syncStaffServices (staffId, serviceIds) {
    const res = await api.put(`${this.baseURL}/staff/${staffId}/services`, { service_ids: serviceIds })
    return res.data
  }

  async listRules (params) {
    const res = await api.get(`${this.baseURL}/availability/rules`, { params })
    return res.data
  }

  async createRule (payload) {
    const res = await api.post(`${this.baseURL}/availability/rules`, payload)
    return res.data
  }

  async syncWeeklyRules (staffId, rules) {
    const res = await api.post(`${this.baseURL}/availability/rules/sync`, {
      staff_id: staffId,
      rules
    })
    return res.data
  }

  async updateRule (id, payload) {
    const res = await api.put(`${this.baseURL}/availability/rules/${id}`, payload)
    return res.data
  }

  async deleteRule (id) {
    await api.delete(`${this.baseURL}/availability/rules/${id}`)
  }

  async listExceptions (params) {
    const res = await api.get(`${this.baseURL}/availability/exceptions`, { params })
    return res.data
  }

  async createException (payload) {
    const res = await api.post(`${this.baseURL}/availability/exceptions`, payload)
    return res.data
  }

  async updateException (id, payload) {
    const res = await api.put(`${this.baseURL}/availability/exceptions/${id}`, payload)
    return res.data
  }

  async deleteException (id) {
    await api.delete(`${this.baseURL}/availability/exceptions/${id}`)
  }

  async collectPayment (appointmentId, payload) {
    const res = await api.post(`${this.baseURL}/appointments/${appointmentId}/payments/collect`, payload)
    return res.data
  }

  async chargeSavedCard (appointmentId, payload) {
    const res = await api.post(`${this.baseURL}/appointments/${appointmentId}/payments/charge-saved-card`, payload)
    return res.data
  }

  async recordManualPayment (appointmentId, payload) {
    const res = await api.post(`${this.baseURL}/appointments/${appointmentId}/payments/manual`, payload)
    return res.data
  }

  async getPaymentLink (appointmentId, params = {}) {
    const res = await api.post(`${this.baseURL}/appointments/${appointmentId}/payments/link`, params)
    return res.data
  }

  async emailPaymentLink (appointmentId, payload) {
    const res = await api.post(`${this.baseURL}/appointments/${appointmentId}/payments/email-link`, payload)
    return res.data
  }

  async smsPaymentLink (appointmentId, payload) {
    const res = await api.post(`${this.baseURL}/appointments/${appointmentId}/payments/sms-link`, payload)
    return res.data
  }
}

export const bookingService = new BookingService()
